import React, { useEffect, useMemo, useState } from 'react'
import { SimpleModal } from 'components/Modal'

interface Props {
  isOpen: boolean
  dateKey: string
  marked: boolean
  onConfirm: (params: { start: string; end: string }) => void
  onRemove?: () => void
  onClose: () => void
  /** T2-5797: show confirm step with Auto-book global settings snapshot */
  globalSettingsPreview?: {
    maxDistance?: number
    minShiftLength?: number
    hourlyRate?: number
  } | null
}

function buildTimeOptions(): string[] {
  const opts: string[] = []
  const pad = (n: number) => String(n).padStart(2, '0')
  for (let h = 5; h <= 23; h++) {
    for (let m = 0; m < 60; m += 15) {
      const label = `${((h % 12) || 12)}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`
      opts.push(label)
    }
  }
  return opts
}

const HoursPickerModal: React.FC<Props> = ({
  isOpen,
  dateKey,
  marked,
  onConfirm,
  onRemove,
  onClose,
  globalSettingsPreview,
}) => {
  const options = useMemo(() => buildTimeOptions(), [])
  const [start, setStart] = useState<string>('')
  const [end, setEnd] = useState<string>('')
  const [step, setStep] = useState<'times' | 'confirm'>('times')

  useEffect(() => {
    if (!isOpen) {
      setStep('times')
      setStart('')
      setEnd('')
    }
  }, [isOpen])

  const isValid = useMemo(() => {
    if (!start || !end) return false
    const idxS = options.indexOf(start)
    const idxE = options.indexOf(end)
    return idxE >= idxS
  }, [start, end, options])

  const summaryLines = useMemo(() => {
    const g = globalSettingsPreview
    if (!g) return []
    const lines: string[] = []
    if (g.hourlyRate != null) lines.push(`Typical rate: $${g.hourlyRate}/hr`)
    if (g.maxDistance != null) lines.push(`Max travel: ${g.maxDistance} km`)
    if (g.minShiftLength != null) lines.push(`Minimum shift length: ${g.minShiftLength} hours`)
    return lines
  }, [globalSettingsPreview])

  return (
    <SimpleModal isOpen={isOpen} onClose={onClose}>
      <div style={{ padding: '1rem', minWidth: 280 }}>
        {step === 'times' ? (
          <>
            <h3 style={{ marginTop: 0 }}>{marked ? 'Edit availability hours' : 'Set availability hours'}</h3>
            <div style={{ color: 'var(--text-secondary, #666)', marginBottom: '0.5rem' }}>Date: {dateKey}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label>
                Earliest start
                <select className="no-fastclick" value={start} onChange={(e) => setStart(e.target.value)} style={{ width: '100%', height: 40, borderRadius: 8, marginTop: 4 }}>
                  <option value="">Select time</option>
                  {options.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label>
                Latest end
                <select className="no-fastclick" value={end} onChange={(e) => setEnd(e.target.value)} style={{ width: '100%', height: 40, borderRadius: 8, marginTop: 4 }}>
                  <option value="">Select time</option>
                  {options.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <button
                type="button"
                className="bigButtonInNewStyle"
                disabled={!isValid}
                onClick={() => setStep('confirm')}
              >
                Next
              </button>
              {marked && onRemove && (
                <button
                  type="button"
                  className="bigButtonInNewStyle"
                  onClick={onRemove}
                  style={{ background: 'var(--error-main, #d32f2f)', color: 'white' }}
                >
                  Remove availability
                </button>
              )}
              <button type="button" className="bigButtonInNewStyle" onClick={onClose}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ marginTop: 0 }}>Confirm Auto-book hours</h3>
            <div style={{ color: 'var(--text-secondary, #666)', marginBottom: '0.75rem' }}>
              <div><strong>Date:</strong> {dateKey}</div>
              <div><strong>Hours:</strong> {start} – {end}</div>
              {summaryLines.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Your Auto-book settings</strong>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                    {summaryLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                className="bigButtonInNewStyle"
                onClick={() => onConfirm({ start, end })}
              >
                Set these hours
              </button>
              <button type="button" className="bigButtonInNewStyle" onClick={() => setStep('times')}>
                Go back
              </button>
            </div>
          </>
        )}
      </div>
    </SimpleModal>
  )
}

export default HoursPickerModal
